/**
 * Phone-Call Lead Detail page (`/admin/ghl-qualifier/phone-call/:id`).
 *
 * Renders a single form/phone-call lead from `ghl_form_call_lead_checks`.
 * Unlike qualified/converted leads there is no Claude analysis timeline — the
 * form-call job only reads the GHL `new phone lead` / `new survey lead` tags
 * and uploads the matching Google Ads conversion. So this page shows the
 * contact, the job status, which tags matched, which events uploaded, and the
 * Google Ads upload detail for the phone_call event.
 */

import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { buildGHLContactUrl, getPhoneCallStatusColor } from '@/lib/ghl-qualifier'
import {
  usePhoneCallLeadDetail,
  useQualifierGoogleAdsSettings,
} from '@/hooks/useGHLQualifierDashboard'
import { GoogleAdsUploadDetail } from '@/components/ghl-qualifier/GoogleAdsUploadDetail'

const EVENT_LABELS: Record<string, string> = {
  phone_call: 'Phone call',
  form_submit: 'Form submit',
}

export default function PhoneCallLeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const detail = usePhoneCallLeadDetail(id)
  // Hoisted so hook order stays stable; the fetch is gated on the locationId.
  const gaSettings = useQualifierGoogleAdsSettings(
    detail.data?.lead.location_id ?? '',
    !!detail.data?.lead.location_id
  )

  const back = () => {
    if (detail.data?.lead.location_id) {
      navigate({
        pathname: `/admin/ghl-qualifier/location/${detail.data.lead.location_id}/phone-call-leads`,
        search: location.search,
      })
    } else {
      navigate({ pathname: '/admin/ghl-qualifier', search: location.search })
    }
  }

  if (detail.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!detail.data) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={back} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="text-lg font-medium">Phone-call lead not found.</p>
            <p className="mt-1 text-sm">It may have been deleted or you may not have access.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { lead, contact } = detail.data
  const directMode = gaSettings.data?.conversion_delivery_method === 'google_ads'
  const status = getPhoneCallStatusColor(lead.status)

  const displayName = contact?.full_name ?? contact?.first_name ?? lead.contact_id
  const email = contact?.email ?? null
  const phone = contact?.phone ?? null
  const ghlUrl = buildGHLContactUrl(lead.location_id, lead.contact_id)
  const tagsFound = lead.tags_found ?? []
  const eventsUploaded = lead.events_uploaded ?? []

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={back} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to feed
      </Button>

      <div>
        <h1 className="text-2xl font-bold">{displayName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {[email, phone, `Contact ${lead.contact_id}`].filter(Boolean).join('  ·  ')}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Created {new Date(lead.created_at).toLocaleString()} · Location:{' '}
          <span className="font-mono">{lead.location_id}</span>
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            Form / phone-call check
            <Badge className={`${status.bg} ${status.text} hover:${status.bg}`}>
              {status.label}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Tags found
            </span>
            {tagsFound.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {tagsFound.map((tag) => (
                  <Badge key={tag} variant="outline" className="w-fit font-normal">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">No matching tags found.</span>
            )}
          </div>

          <div className="grid gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Events uploaded
            </span>
            {eventsUploaded.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {eventsUploaded.map((evt) => (
                  <Badge key={evt} variant="secondary" className="w-fit">
                    {EVENT_LABELS[evt] ?? evt}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">None uploaded.</span>
            )}
          </div>

          {lead.last_outcome && (
            <div className="grid gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Last outcome
              </span>
              <span>{lead.last_outcome}</span>
            </div>
          )}

          {lead.error_message && (
            <div className="grid gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Error
              </span>
              <span className="text-destructive">{lead.error_message}</span>
            </div>
          )}

          <div className="grid gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Last evaluated
            </span>
            <span>
              {lead.last_evaluated_at
                ? new Date(lead.last_evaluated_at).toLocaleString()
                : '—'}
            </span>
          </div>
        </CardContent>
      </Card>

      <GoogleAdsUploadDetail
        contactId={lead.contact_id}
        eventType="phone_call"
        enabled={directMode}
      />

      <div className="flex flex-wrap gap-2 pt-2">
        <Button asChild variant="outline" size="sm" className="gap-2">
          <a href={ghlUrl} target="_blank" rel="noopener noreferrer">
            View in GHL
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </div>
  )
}
